import { Check, CircleAlert } from "lucide-react";
import { ReactNode } from "react";
import { DateInput } from "@/components/ui/date-input";
import { DomainTermInfo } from "@/components/ui/domain-term-info";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

export function StepHead({ title, desc }: { title: string; desc: string }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{desc}</p>
    </div>
  );
}

function FieldLabel({ htmlFor, children, required, term }: {
  htmlFor?: string; children: ReactNode; required?: boolean; term?: string;
}) {
  return (
    <Label htmlFor={htmlFor} className="mb-1.5 inline-flex items-center gap-1">
      <span>{children}</span>
      {required && <span className="text-destructive">*</span>}
      {term && <DomainTermInfo termId={term} />}
    </Label>
  );
}

interface TextFieldProps {
  id: string; label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; prefix?: string; type?: string; required?: boolean; term?: string; helper?: string;
  /** Inline validation message; when set, the field renders in an error state. */
  error?: string;
}

export function TextField({ id, label, value, onChange, placeholder, prefix, type = "text", required, term, helper, error }: TextFieldProps) {
  const describedBy = error ? `${id}-error` : helper ? `${id}-helper` : undefined;
  return (
    <div>
      <FieldLabel htmlFor={id} required={required} term={term}>{label}</FieldLabel>
      <div className="relative">
        {prefix && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{prefix}</span>
        )}
        <Input id={id} type={type} value={value} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error ? true : undefined} aria-describedby={describedBy}
          className={cn(prefix && "pl-7", error && "border-destructive focus-visible:ring-destructive")} />
      </div>
      {error ? (
        <p id={`${id}-error`} className="mt-1.5 text-xs text-destructive">{error}</p>
      ) : helper ? (
        <p id={`${id}-helper`} className="mt-1.5 text-xs text-muted-foreground">{helper}</p>
      ) : null}
    </div>
  );
}

export function DateField({ id, label, value, onChange, required, term }: {
  id: string; label: string; value: string; onChange: (v: string) => void; required?: boolean; term?: string;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id} required={required} term={term}>{label}</FieldLabel>
      <DateInput id={id} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function SelectField({ id, label, value, onChange, options, placeholder = "Select…", required }: {
  id: string; label: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[]; placeholder?: string; required?: boolean;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id} required={required}>{label}</FieldLabel>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function SectionDivider({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 pt-1">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

export function ResultBanner({ variant, title, desc }: { variant: "pass" | "fail"; title: string; desc: string }) {
  const pass = variant === "pass";
  const Icon = pass ? Check : CircleAlert;
  return (
    <div className={cn("flex items-center gap-3 rounded-lg border px-4 py-3.5",
      pass ? "border-emerald-500/30 bg-emerald-500/10" : "border-destructive/30 bg-destructive/10")}>
      <span className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
        pass ? "bg-emerald-500/20 text-emerald-500" : "bg-destructive/20 text-destructive")}>
        <Icon className="h-4 w-4" strokeWidth={2.25} />
      </span>
      <div>
        <p className={cn("text-sm font-semibold", pass ? "text-emerald-500" : "text-destructive")}>{title}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
    </div>
  );
}

export function YesNoToggle({ value, onChange, yesLabel = "Yes", noLabel = "No" }: {
  value: "" | "yes" | "no"; onChange: (v: "yes" | "no") => void; yesLabel?: string; noLabel?: string;
}) {
  const opt = (val: "yes" | "no", label: string) => {
    const selected = value === val;
    return (
      <button type="button" onClick={() => onChange(val)} aria-pressed={selected}
        className={cn("flex h-10 flex-1 items-center justify-center gap-2 rounded-md border text-sm font-medium transition-colors",
          selected ? "border-primary bg-primary/10 text-foreground" : "border-border bg-background text-muted-foreground hover:border-input")}>
        <span className={cn("flex h-4 w-4 items-center justify-center rounded-full border",
          selected ? "border-primary bg-primary" : "border-input")}>
          {selected && <Check className="h-2.5 w-2.5 text-primary-foreground" strokeWidth={3} />}
        </span>
        {label}
      </button>
    );
  };
  return <div className="flex gap-3">{opt("yes", yesLabel)}{opt("no", noLabel)}</div>;
}
